import {
  HistoryOutlined,
  InsertDriveFile,
  Vaccines,
} from "@mui/icons-material";
import MedicationIcon from "@mui/icons-material/Medication";
import ThermostatIcon from "@mui/icons-material/Thermostat";
import { Box, Paper, Tab, Tabs } from "@mui/material";
import { useContext } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router-dom";
import TextEditorViewer from "../../../../../components/display/TextEditorViewer";
import { GlobalDataContext } from "../../../../../contexts/GlobalData";
import { STATUSES } from "../../../../../enums/AppointmentStatus";
import {
  CREATE_TREATMENT,
  EDIT_TREATMENT,
  VIEW_AUDITING,
} from "../../../../../enums/PermissionTitles";
import useUser from "../../../../../hooks/useUser";
import AppointmentBiometrics from "../../../../biometrics/components/AppointmentBiometrics";
import AppointmentFiles from "../../../components/display/AppointmentFiles";
import AppointmentDeltas from "../../../components/revisions/AppointmentDeltas";
import ListOfVaccines from "../../../components/tabs/components/ListOfVaccines";
import ToggleFileUpload from "../../../context/ToggleFileUpload";

const TAB_PARAM = "appointmentTab";

const isFinished = (appointment) =>
  String(appointment?.status).toUpperCase() === STATUSES.FINISHED;

/**
 * Every tab of the single appointment view, in display order. `visible`
 * receives the appointment and `{ roles, currentClinic, isPermissionAllowed }`.
 */
const APPOINTMENT_TABS = [
  {
    key: "Files",
    icon: <InsertDriveFile />,
    testId: "appointment.details.tabs.files",
    Component: FilesTab,
    visible: (appointment) =>
      [STATUSES.ACTIVE, STATUSES.FINISHED, STATUSES.RESOLVED].includes(
        appointment?.status,
      ),
  },
  {
    key: "Treatment",
    icon: <MedicationIcon />,
    testId: "appointment.details.tabs.treatment",
    Component: TreatmentTab,
    visible: (appointment, { roles, isPermissionAllowed }) =>
      Boolean(appointment?.treatment?.description) &&
      (roles.user ||
        isPermissionAllowed(CREATE_TREATMENT) ||
        isPermissionAllowed(EDIT_TREATMENT)),
  },
  {
    key: "Biometrics",
    icon: <ThermostatIcon />,
    testId: "appointment.details.tabs.biometrics",
    Component: BiometricsTab,
    visible: isFinished,
  },
  {
    key: "Vaccines",
    icon: <Vaccines />,
    testId: "appointment.details.tabs.vaccines",
    Component: ListOfVaccines,
    visible: isFinished,
  },
  {
    key: "History",
    icon: <HistoryOutlined />,
    testId: "appointment.details.tabs.history",
    Component: AppointmentDeltas,
    pushRight: true,
    visible: (appointment, { roles, currentClinic, isPermissionAllowed }) =>
      !roles.user &&
      isPermissionAllowed(VIEW_AUDITING) &&
      (roles.master ||
        (appointment?.clinic?.id != null &&
          appointment.clinic.id === currentClinic?.id)),
  },
];

/**
 * Tabs below the banner. Only the selected tab is mounted, so each tab
 * fetches its own data when it's opened. The selection lives in the URL and
 * falls back to the first visible tab; changing it keeps `location.state`,
 * which holds the back button's target.
 */
export default function AppointmentTabs({ appointment }) {
  const { t } = useTranslation();
  const { roles, currentClinic } = useUser();
  const { isPermissionAllowed } = useContext(GlobalDataContext);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  const tabs = APPOINTMENT_TABS.filter((tab) =>
    tab.visible(appointment, { roles, currentClinic, isPermissionAllowed }),
  );

  if (tabs.length === 0) {
    return null;
  }

  const selectedIndex = Math.max(
    0,
    tabs.findIndex((tab) => tab.key === searchParams.get(TAB_PARAM)),
  );
  const { Component } = tabs[selectedIndex];

  function handleTabChange(_, index) {
    setSearchParams(
      (params) => {
        params.set(TAB_PARAM, tabs[index].key);
        return params;
      },
      { replace: true, state: location.state },
    );
  }

  return (
    <Box mt={2}>
      <Paper sx={{ borderRadius: 3 }}>
        <Tabs
          value={selectedIndex}
          onChange={handleTabChange}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          {tabs.map((tab) => (
            <Tab
              key={tab.key}
              icon={tab.icon}
              label={t(tab.key)}
              data-testid={tab.testId}
              sx={{
                minHeight: 64,
                minWidth: 120,
                textTransform: "none",
                fontSize: 12,
                ...(tab.pushRight && { ml: "auto" }),
              }}
            />
          ))}
        </Tabs>
      </Paper>
      <Box mt={2}>
        <Component appointment={appointment} />
      </Box>
    </Box>
  );
}

function FilesTab() {
  return (
    <ToggleFileUpload>
      <Paper sx={{ p: 2 }}>
        <AppointmentFiles />
      </Paper>
    </ToggleFileUpload>
  );
}

function TreatmentTab({ appointment }) {
  return (
    <Paper sx={{ p: 1 }}>
      <TextEditorViewer value={appointment?.treatment?.description} />
    </Paper>
  );
}

function BiometricsTab() {
  return <AppointmentBiometrics showForm={false} />;
}
